import React from 'react';
import { Code, Zap, Shield, Smartphone, ArrowRight, MessageCircle, CheckCircle2, Cpu, Globe, Server } from 'lucide-react';
import { Button } from '../components/ui/button';
import WebDevelopmentProcess from '../components/WebDevelopmentProcess';
import { Link } from 'react-router-dom';

const WebDevelopment = () => {
  const features = [
    {
      id: 'stack',
      icon: Code,
      title: 'Modern Tech Stack',
      badge: 'React 18 & Next.js 14',
      description: 'Built with React 18, Next.js 14, TypeScript, and serverless microservices for maximum resilience.',
      color: 'from-orange-500 to-amber-600',
      badgeColor: 'text-orange-400 bg-orange-500/10 border-orange-500/30',
      figureType: 'code'
    },
    {
      id: 'speed',
      icon: Zap,
      title: 'Sub-Second Lightning Speed',
      badge: 'PageSpeed 98+ Score',
      description: 'Optimized with server-side rendering (SSR), Vercel edge CDN caching, and Core Web Vitals tuning.',
      color: 'from-cyan-500 to-blue-600',
      badgeColor: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30',
      figureType: 'speed'
    },
    {
      id: 'security',
      icon: Shield,
      title: 'Secure & Enterprise Scalable',
      badge: 'OWASP Security Audit',
      description: 'Enterprise-grade SSL security hardening, automated Cypress E2E tests, and 99.9% uptime infrastructure.',
      color: 'from-purple-500 to-indigo-600',
      badgeColor: 'text-purple-400 bg-purple-500/10 border-purple-500/30',
      figureType: 'security'
    },
    {
      id: 'responsive',
      icon: Smartphone,
      title: 'Fully Responsive UX',
      badge: 'WCAG 2.1 Compliant',
      description: 'Flawless human-centered design across desktop, tablet, and mobile with WCAG 2.1 accessibility.',
      color: 'from-emerald-500 to-teal-600',
      badgeColor: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
      figureType: 'responsive'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-950 pt-20 text-left">
      {/* Hero Section with Dark Shade Digital Background */}
      <section className="relative py-24 md:py-32 border-b border-gray-800/80 overflow-hidden">

        {/* Background Digital Graphic Image & Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/dark_web_engineering_header_bg.png"
            alt="Custom Web Engineering Dark Background"
            className="w-full h-full object-cover filter brightness-90 scale-105"
          />
          {/* Subtle Dark Shade Gradient Vignettes */}
          <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-gray-950/50 to-gray-950" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950/90 via-gray-950/40 to-gray-950/90" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">

            {/* Telemetry Badge */}
            <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-4 py-1.5 mb-6 backdrop-blur-md">
              <span className="text-cyan-400 text-sm font-mono font-bold uppercase tracking-wider">
                Full-Cycle Web Engineering & Architecture
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight tracking-tight drop-shadow-2xl">
              Custom Web Engineering <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">Solutions</span>
            </h1>

            {/* Subtitle */}
            <p className="text-base md:text-xl text-gray-300 mb-8 leading-relaxed font-normal max-w-3xl mx-auto drop-shadow">
              Transform your digital vision into scalable web applications with our expert engineering team.
              We craft high-performing websites, SaaS platforms, and enterprise portals tailored to your roadmap.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button className="bg-gradient-to-r from-orange-500 via-amber-500 to-cyan-500 hover:from-orange-600 hover:to-cyan-600 text-white px-8 py-6 text-base font-extrabold rounded-xl shadow-xl hover:shadow-orange-500/30 transition-all duration-300">
                  Start Your Project
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/portfolio">
                <Button variant="outline" className="bg-gray-900/80 hover:bg-gray-800 border border-gray-700 text-gray-200 px-8 py-6 text-base font-bold rounded-xl backdrop-blur-md">
                  View Portfolio
                </Button>
              </Link>
            </div>

          </div>
        </div>

        {/* Ambient Dark Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />
      </section>

      {/* Widget Boxes Section: Why Choose Our Web Development Services */}
      <section className="py-24 bg-gray-950 border-t border-b border-gray-800 relative overflow-hidden text-left">
        {/* Ambient Background Glow */}
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="container mx-auto px-6 relative z-10">

          {/* Header */}
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 py-1.5 mb-4">
              <span className="text-orange-400 text-sm font-mono font-bold uppercase tracking-wider">
                WEB ENGINEERING PILLARS & TELEMETRY
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Why Choose Our <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">Web Development Services</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              Cutting-edge full-stack technology meets exceptional human-centered user experience design.
            </p>
          </div>

          {/* 4 Interactive Widget Boxes Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group relative bg-gray-900/90 border border-gray-800 rounded-3xl p-6 hover:border-cyan-500/70 transition-all duration-500 hover:-translate-y-2 shadow-2xl hover:shadow-cyan-500/10 flex flex-col justify-between"
              >
                <div>
                  {/* Top Bar: Icon & Pill Badge */}
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300`}>
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <span className={`text-[11x] font-mono font-bold px-2.5 py-1 rounded-full border ${feature.badgeColor}`}>
                      {feature.badge}
                    </span>
                  </div>

                  {/* Widget Box Title */}
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                    {feature.title}
                  </h3>

                  {/* Figure Graphic Widget */}
                  {feature.figureType === 'code' && (
                    <div className="bg-gray-950 rounded-2xl p-3.5 border border-gray-800/80 font-mono text-sm my-4 space-y-1.5 shadow-inner">
                      <div className="flex items-center justify-between pb-2 border-b border-gray-800/80 text-[11px]">
                        <span className="text-orange-400 font-bold">tech-stack.ts</span>
                        <span className="text-emerald-400 font-bold bg-emerald-500/10 px-1.5 py-0.5 rounded">PASSED</span>
                      </div>
                      <div className="text-cyan-300 font-semibold text-[11px] truncate">$ const stack = ['Next.js 14', 'React 18']</div>
                      <div className="text-gray-400 text-[11px] truncate">$ api: GraphQL & TypeScript Microservices</div>
                    </div>
                  )}

                  {feature.figureType === 'speed' && (
                    <div className="bg-gray-950 rounded-2xl p-3.5 border border-gray-800/80 font-mono text-sm my-4 space-y-2 shadow-inner">
                      <div className="flex justify-between text-[11px]">
                        <span className="text-gray-400">Google PageSpeed</span>
                        <span className="text-emerald-400 font-bold">98 / 100</span>
                      </div>
                      <div className="w-full bg-gray-800 h-2 rounded-full overflow-hidden">
                        <div className="bg-gradient-to-r from-cyan-400 to-emerald-400 h-full w-[98%]" />
                      </div>
                      <div className="flex justify-between text-[11px] text-gray-400 pt-0.5">
                        <span>LCP: 0.8s</span>
                        <span>FID: 12ms</span>
                        <span>CLS: 0.00</span>
                      </div>
                    </div>
                  )}

                  {feature.figureType === 'security' && (
                    <div className="bg-gray-950 rounded-2xl p-3.5 border border-gray-800/80 font-mono text-sm my-4 space-y-2 shadow-inner">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-gray-400">OWASP Audit</span>
                        <span className="text-purple-400 font-bold">PASSED</span>
                      </div>
                      <div className="flex items-center space-x-2 text-[11px] text-emerald-400 bg-emerald-500/10 p-2 rounded-xl border border-emerald-500/20">
                        <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="font-semibold">256-Bit SSL Armor Active</span>
                      </div>
                    </div>
                  )}

                  {feature.figureType === 'responsive' && (
                    <div className="bg-gray-950 rounded-2xl p-3.5 border border-gray-800/80 font-mono text-sm my-4 space-y-2 shadow-inner">
                      <div className="flex items-center justify-between text-[11px] text-gray-400">
                        <span>Breakpoint Grid</span>
                        <span className="text-cyan-400 font-bold">Fluid UI</span>
                      </div>
                      <div className="grid grid-cols-3 gap-1.5 text-[11px] text-center font-bold">
                        <span className="bg-gray-900 border border-gray-800 py-1 rounded text-gray-300">Desktop</span>
                        <span className="bg-gray-900 border border-gray-800 py-1 rounded text-gray-300">Tablet</span>
                        <span className="bg-gray-900 border border-gray-800 py-1 rounded text-emerald-400">Mobile</span>
                      </div>
                    </div>
                  )}

                  <p className="text-gray-400 text-sm leading-relaxed font-normal">
                    {feature.description}
                  </p>
                </div>

              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Dedicated 6-Stage Web Engineering Process Component */}
      <WebDevelopmentProcess />

      {/* Full-Section Enterprise CTA Section */}
      <section className="py-24 bg-gradient-to-b from-gray-950 via-slate-900 to-gray-950 relative overflow-hidden border-t border-gray-800">

        {/* Background Ambient Glow & Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1f293712_1px,transparent_1px),linear-gradient(to_bottom,#1f293712_1px,transparent_1px)] bg-[size:3rem_3rem] pointer-events-none" />
        <div className="absolute top-1/3 left-10 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

            {/* Left Content Column */}
            <div className="lg:col-span-7 text-left space-y-6">
              <div className="inline-flex items-center space-x-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5">
                <span className="text-orange-400 text-sm font-semibold uppercase tracking-wider">
                  Full-Cycle Web Architecture
                </span>
              </div>

              <h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight">
                Ready to Build Your <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">Web Application?</span>
              </h2>

              <p className="text-gray-300 text-base md:text-lg leading-relaxed">
                Partner with our senior technical architects to engineer resilient web platforms, high-throughput microservices, and conversion-focused web applications tailored to your business goals.
              </p>

              {/* 4 Feature Value Props */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="flex items-start space-x-3 bg-gray-900/80 border border-gray-800 rounded-xl p-3.5">
                  <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-white mb-0.5">Free Architecture Audit</h4>
                    <p className="text-[11px] text-gray-400">Complimentary tech stack & roadmap strategy session.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 bg-gray-900/80 border border-gray-800 rounded-xl p-3.5">
                  <CheckCircle2 className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-white mb-0.5">Strict Non-Disclosure</h4>
                    <p className="text-[11px] text-gray-400">100% mutual NDA & complete code ownership.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 bg-gray-900/80 border border-gray-800 rounded-xl p-3.5">
                  <CheckCircle2 className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-white mb-0.5">Dedicated Scrum Team</h4>
                    <p className="text-[11px] text-gray-400">Direct developer access & weekly sprint demos.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 bg-gray-900/80 border border-gray-800 rounded-xl p-3.5">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-white mb-0.5">SLA-Backed Security</h4>
                    <p className="text-[11px] text-gray-400">Continuous vulnerability audits & 24/7 support.</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center gap-4 pt-4">
                <Link to="/contact" className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-base font-bold rounded-xl shadow-lg shadow-orange-500/20 hover:scale-105 transition-transform duration-300">
                    Get Started Today
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a
                  href="https://wa.me/918796118101?text=Hello!%20I%20want%20to%20discuss%20a%20Web%20Development%20project."
                  target="_blank"
                  rel="noreferrer"
                  className="w-full sm:w-auto"
                >
                  <Button variant="outline" className="w-full sm:w-auto border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 px-8 py-4 text-base font-bold rounded-xl flex items-center justify-center gap-2">
                    <MessageCircle className="w-5 h-5 text-emerald-400" />
                    Talk To Expert (+91 8796 118 101)
                  </Button>
                </a>
              </div>
            </div>

            {/* Right Interactive Mockup Graphic */}
            <div className="lg:col-span-5 relative">
              <div className="bg-gray-900 border border-gray-800 rounded-3xl p-3 shadow-2xl relative overflow-hidden group">

                {/* Mock Browser Header */}
                <div className="flex items-center justify-between px-4 py-2.5 bg-gray-950 rounded-2xl mb-3 border border-gray-800">
                  <div className="flex items-center space-x-1.5">
                    <span className="w-3 h-3 rounded-full bg-red-500/80 inline-block" />
                    <span className="w-3 h-3 rounded-full bg-yellow-500/80 inline-block" />
                    <span className="w-3 h-3 rounded-full bg-emerald-500/80 inline-block" />
                  </div>
                  <div className="bg-gray-900 text-gray-400 text-[11px] font-mono px-4 py-1 rounded-lg border border-gray-800 truncate max-w-[200px]">
                    app.hoasp.com
                  </div>
                  <Globe className="w-4 h-4 text-cyan-400" />
                </div>

                {/* Mockup Preview Image */}
                <div className="relative h-72 md:h-80 rounded-2xl overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&q=80"
                    alt="Web Development Code Architecture"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/40 to-transparent" />

                  {/* Floating Metric Badge 1 */}
                  <div className="absolute top-4 right-4 bg-gray-950/90 backdrop-blur-md border border-gray-800 px-3 py-1.5 rounded-xl shadow-xl flex items-center space-x-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                    <span className="text-[11px] font-mono font-bold text-white">99.9% Uptime SLA</span>
                  </div>

                  {/* Floating Metric Badge 2 */}
                  <div className="absolute bottom-4 left-4 bg-gray-950/90 backdrop-blur-md border border-gray-800 px-3 py-2 rounded-xl shadow-xl flex items-center space-x-3">
                    <Server className="w-4 h-4 text-cyan-400" />
                    <div className="text-left">
                      <p className="text-[11px] text-gray-400 uppercase font-semibold">Latency</p>
                      <p className="text-sm font-mono font-bold text-emerald-400">&lt;100ms Worldwide</p>
                    </div>
                  </div>

                  {/* Tech Stack Pills Overlay */}
                  <div className="absolute bottom-4 right-4 hidden sm:flex items-center space-x-1.5">
                    <span className="bg-orange-500/90 text-white text-[11px] font-bold px-2 py-0.5 rounded-md shadow">React 18</span>
                    <span className="bg-cyan-500/90 text-white text-[11px] font-bold px-2 py-0.5 rounded-md shadow">Next.js 14</span>
                    <span className="bg-amber-500/90 text-white text-[11px] font-bold px-2 py-0.5 rounded-md shadow">AWS</span>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>

      </section>
    </div>
  );
};

export default WebDevelopment;
